import React from 'react';
import { Plus, Trash2, Clock, Radio, Hand, Link2 } from 'lucide-react';
import { Button, Input, Select, ScheduleField } from '../../../components/ui';
import { Label } from '../../../components/ui';
import type { WizardState } from './hire-types.js';

interface HireStepTriggersProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

type TriggerType = 'cron' | 'event' | 'manual' | 'chained';

const TRIGGER_META: Record<TriggerType, { label: string; icon: React.ReactNode }> = {
  cron: { label: 'Scheduled (Cron)', icon: <Clock size={14} /> },
  event: { label: 'Event-driven', icon: <Radio size={14} /> },
  manual: { label: 'Manual only', icon: <Hand size={14} /> },
  chained: { label: 'Chained', icon: <Link2 size={14} /> },
};

export const HireStepTriggers: React.FC<HireStepTriggersProps> = ({ state, onChange }) => {
  const addTrigger = () => {
    onChange({
      triggers: [...state.triggers, { type: 'manual' }],
    });
  };

  const updateTrigger = (index: number, updates: Partial<WizardState['triggers'][number]>) => {
    const next = state.triggers.map((t, i) => (i === index ? { ...t, ...updates } : t));
    onChange({ triggers: next });
  };

  const removeTrigger = (index: number) => {
    onChange({ triggers: state.triggers.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-heading">Triggers</h4>
          <p className="text-xs text-faint">Define when this daemon should run.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={addTrigger}>
          <Plus size={14} /> Add Trigger
        </Button>
      </div>

      {state.triggers.length === 0 && (
        <div className="p-6 text-center bg-surface-alt rounded-lg border border-dashed">
          <Hand size={24} className="mx-auto text-faint mb-2" />
          <p className="text-sm text-muted">No triggers configured</p>
          <p className="text-xs text-faint mt-1">This daemon will only run when triggered manually.</p>
        </div>
      )}

      <div className="space-y-3">
        {state.triggers.map((trigger, i) => (
          <div key={`trigger-${i}`} className="p-3 bg-surface-alt rounded-lg border space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted">
                {TRIGGER_META[trigger.type as TriggerType]?.icon}
                <span className="font-medium text-heading">Trigger {i + 1}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Remove trigger ${i + 1}`}
                onClick={() => removeTrigger(i)}
              >
                <Trash2 size={14} />
              </Button>
            </div>

            <div>
              <Label>Type</Label>
              <Select
                value={trigger.type}
                onChange={(e) =>
                  updateTrigger(i, {
                    type: e.target.value,
                    cron: undefined,
                    eventPattern: undefined,
                    chainDaemonId: undefined,
                  })
                }
              >
                {Object.entries(TRIGGER_META).map(([value, meta]) => (
                  <option key={value} value={value}>
                    {meta.label}
                  </option>
                ))}
              </Select>
            </div>

            {trigger.type === 'cron' && (
              <ScheduleField
                label="Cron Schedule"
                value={trigger.cron ?? ''}
                onChange={(cron) => updateTrigger(i, { cron })}
              />
            )}

            {trigger.type === 'event' && (
              <div>
                <Label hint="e.g. daemon:run:completed">Event Pattern</Label>
                <Input
                  value={trigger.eventPattern ?? ''}
                  onChange={(e) => updateTrigger(i, { eventPattern: e.target.value })}
                  placeholder="daemon:run:completed"
                />
              </div>
            )}

            {trigger.type === 'chained' && (
              <div>
                <Label>Chain After Daemon ID</Label>
                <Input
                  type="number"
                  value={trigger.chainDaemonId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateTrigger(i, { chainDaemonId: val ? Number(val) : undefined });
                  }}
                  placeholder="Daemon ID to chain after"
                />
              </div>
            )}

            {trigger.type === 'manual' && (
              <p className="text-xs text-faint italic">This daemon will only run when triggered manually.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
